import React from 'react';
import * as Headless from '@headlessui/react';
import clsx from 'clsx';
import { ArrowUpIcon, ArrowDownIcon, QueueListIcon } from '@heroicons/react/16/solid';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@oysterjs/ui/table';
import { Spinner } from '@oysterjs/ui/spinner';

// ---------------------------------------------------------------------------
// Sort / Group primitives
// ---------------------------------------------------------------------------

export type SortDirection = 'asc' | 'desc';

// Matches the card it floats over, so rows scrolling under it stay hidden. A darker
// value would read as a band sitting on the card rather than part of it.
const STICKY_HEADER_CLASS = 'sticky top-0 z-10 bg-white dark:bg-neutral-800';

export const SortHeader = <GroupBy, SortBy>(
  props: React.PropsWithChildren<{
    groupBy?: GroupBy;
    currentGroupBy?: GroupBy | null;
    sortBy: SortBy;
    currentSortBy: SortBy;
    currentSortDirection: SortDirection;
    onSort: (sortBy: SortBy, sortDirection: SortDirection) => void;
    onGroup?: (groupBy: GroupBy | null) => void;
    align?: 'left' | 'right';
    className?: string;
  }>
) => {
  const isActive = props.sortBy === props.currentSortBy;
  const nextDirection = isActive && props.currentSortDirection === 'asc' ? 'desc' : 'asc';
  const isGrouped = props.groupBy === props.currentGroupBy;
  const sortTitle = props.sortBy
    ? `Sort by ${props.sortBy} ${nextDirection === 'asc' ? 'ascending' : 'descending'}`
    : undefined;
  const groupTitle = props.groupBy ? `Group by ${props.groupBy}` : undefined;

  return (
    <TableHeader className={clsx('py-1.5 px-3 text-xs', props.className)}>
      <div
        className={clsx(
          'flex gap-1 items-center w-full',
          props.align === 'right' ? 'text-right flex-row-reverse' : 'flex-row'
        )}
      >
        <Headless.Button
          className={clsx(
            'flex items-center gap-1',
            isActive
              ? 'text-primary-600 dark:text-primary-400'
              : 'text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200'
          )}
          title={sortTitle}
          onClick={() => props.onSort?.(props.sortBy, nextDirection)}
        >
          <div>{props.children}</div>
          {isActive && props.currentSortDirection === 'asc' ? (
            <ArrowUpIcon className="size-3" />
          ) : (
            <ArrowDownIcon className="size-3" />
          )}
        </Headless.Button>
        {props.onGroup && props.groupBy && (
          <Headless.Button
            onClick={() => props.onGroup!(isGrouped ? null : props.groupBy!)}
            title={groupTitle}
            className="group"
          >
            <QueueListIcon
              className={clsx(
                'size-3',
                isGrouped
                  ? 'text-amber-600 dark:text-amber-500'
                  : 'text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200 group-hover:text-neutral-700 dark:group-hover:text-neutral-200'
              )}
            />
          </Headless.Button>
        )}
      </div>
    </TableHeader>
  );
};

// ---------------------------------------------------------------------------
// Column definition
// ---------------------------------------------------------------------------

interface ColumnDefBase<T, SortKey extends string = string, GroupKey extends string = string> {
  id: string;
  sortKey?: SortKey;
  groupKey?: GroupKey;
  align?: 'left' | 'right';
  cellClassName?: string;
  /**
   * The cell carries its own control, so the row's action must not fire from
   * it: no link overlay is laid over it, and a click inside it does not reach
   * onRowClick. Without the latter a checkbox in a row that also toggles on
   * click would toggle twice and land where it started.
   */
  noHref?: boolean;
  renderCell: (item: T) => React.ReactNode;
}

interface ColumnDefWithHeader<
  T,
  SortKey extends string = string,
  GroupKey extends string = string
> extends ColumnDefBase<T, SortKey, GroupKey> {
  header: string;
  renderHeader?: never;
  headerClassName?: string;
}

interface ColumnDefWithRenderHeader<
  T,
  SortKey extends string = string,
  GroupKey extends string = string
> extends ColumnDefBase<T, SortKey, GroupKey> {
  header?: never;
  renderHeader: () => React.ReactNode;
  headerClassName?: never;
}

export type ColumnDef<T, SortKey extends string = string, GroupKey extends string = string> =
  | ColumnDefWithHeader<T, SortKey, GroupKey>
  | ColumnDefWithRenderHeader<T, SortKey, GroupKey>;

// ---------------------------------------------------------------------------
// DataTable
// ---------------------------------------------------------------------------

export interface DataTableProps<
  T,
  SortKey extends string = string,
  GroupKey extends string = string
> {
  data: T[];
  columns: ColumnDef<T, SortKey, GroupKey>[];
  keyExtractor: (item: T) => string;
  rowHref?: (item: T) => string | undefined;
  rowClassName?: string;
  tableClassName?: string;

  // Sorting (controlled, opt-in)
  sortKey?: SortKey;
  sortDirection?: SortDirection;
  onSort?: (key: SortKey, direction: SortDirection) => void;
  sortFn?: (data: T[], key: SortKey, direction: SortDirection) => T[];

  // Grouping (controlled, opt-in)
  groupKey?: GroupKey | null;
  onGroupChange?: (key: GroupKey | null) => void;
  groupFn?: (data: T[], key: GroupKey) => { label: string; items: T[] }[];
  renderGroupRow?: (group: { label: string; items: T[] }) => React.ReactNode;

  // Loading overlay
  loading?: boolean;

  /** Pin the header row while the table body scrolls. */
  stickyHeader?: boolean;
  /** Wrap the table in a vertical scroll container (use with maxHeight). */
  scrollable?: boolean;
  maxHeight?: React.CSSProperties['maxHeight'];

  onRowClick?: (item: T) => void;
}

export const DataTable = <T, SortKey extends string = string, GroupKey extends string = string>(
  props: DataTableProps<T, SortKey, GroupKey>
) => {
  const {
    data,
    columns,
    keyExtractor,
    rowHref,
    rowClassName,
    tableClassName,
    sortKey,
    sortDirection = 'asc',
    onSort,
    sortFn,
    groupKey,
    onGroupChange,
    groupFn,
    renderGroupRow,
    loading,
    stickyHeader,
    scrollable,
    maxHeight,
    onRowClick
  } = props;

  const headerCellClassName = stickyHeader ? STICKY_HEADER_CLASS : undefined;

  const sorted = React.useMemo(
    () => (sortFn && sortKey ? sortFn(data, sortKey, sortDirection) : data),
    [data, sortKey, sortDirection, sortFn]
  );

  const groups = React.useMemo<{ label: string; items: T[] }[]>(
    () => (groupFn && groupKey ? groupFn(sorted, groupKey) : [{ label: '', items: sorted }]),
    [sorted, groupKey, groupFn]
  );

  const renderHeaderCell = (col: ColumnDef<T, SortKey, GroupKey>) => {
    if (col.renderHeader) {
      const header = col.renderHeader();
      // A pinned header row needs every cell pinned, the custom ones too.
      const pinnedHeader =
        headerCellClassName && React.isValidElement<{ className?: string }>(header)
          ? React.cloneElement(header, {
              className: clsx(header.props.className, headerCellClassName)
            })
          : header;
      return <React.Fragment key={col.id}>{pinnedHeader}</React.Fragment>;
    }
    if (col.sortKey && onSort) {
      return (
        <SortHeader
          key={col.id}
          sortBy={col.sortKey}
          currentSortBy={sortKey as SortKey}
          currentSortDirection={sortDirection}
          onSort={onSort}
          align={col.align}
          groupBy={col.groupKey}
          currentGroupBy={groupKey}
          onGroup={col.groupKey ? onGroupChange : undefined}
          className={clsx(col.headerClassName, headerCellClassName)}
        >
          {col.header}
        </SortHeader>
      );
    }
    return (
      <TableHeader
        key={col.id}
        className={clsx('py-1.5 px-3 text-xs', col.headerClassName, headerCellClassName)}
      >
        {col.header}
      </TableHeader>
    );
  };

  const renderGroup = (group: { label: string; items: T[] }) => {
    if (renderGroupRow) {
      return renderGroupRow(group);
    }
    return (
      <tr>
        <th
          colSpan={columns.length}
          className="border-b border-neutral-200 dark:border-neutral-700 bg-neutral-100 dark:bg-neutral-700 py-1.5 px-[var(--gutter,theme(spacing.2))] text-xs font-medium text-neutral-700 dark:text-neutral-300"
        >
          {group.label}
        </th>
      </tr>
    );
  };

  return (
    // isolate keeps the table's own z-10 layers (sticky header, loading cover, row actions
    // lifted above the row link) inside the table, under a popover the page opens over it.
    <div className="relative isolate">
      {loading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/50 dark:bg-neutral-800/50">
          <Spinner size="size-12" color="neutral" />
        </div>
      )}
      <div
        className={clsx(loading && 'opacity-50', scrollable && 'overflow-auto')}
        style={scrollable && maxHeight != null ? { maxHeight } : undefined}
      >
        <Table bleed dense stickyHeader={stickyHeader} className={tableClassName}>
          <TableHead>
            <TableRow>{columns.map(renderHeaderCell)}</TableRow>
          </TableHead>
          <TableBody>
            {groups.map((group) => (
              <React.Fragment key={group.label || '__ungrouped'}>
                {group.label && renderGroup(group)}
                {group.items.map((item) => (
                  <TableRow
                    key={keyExtractor(item)}
                    href={rowHref?.(item)}
                    className={clsx(
                      rowClassName,
                      onRowClick &&
                        !rowHref &&
                        'hover:bg-neutral-950/[2.5%] dark:hover:bg-white/[2.5%]'
                    )}
                    onClick={onRowClick ? () => onRowClick(item) : undefined}
                  >
                    {columns.map((col) => (
                      <TableCell
                        key={col.id}
                        className={col.cellClassName ?? 'py-1.5 px-3'}
                        noHref={col.noHref}
                        onClick={col.noHref && onRowClick ? (e) => e.stopPropagation() : undefined}
                      >
                        {col.renderCell(item)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
